import {
	findPageByFieldId,
	getPageNumber,
} from '../shared/dom-pages.js';
import {
	getFormIdFromElement,
	readCurrentPageNumber,
	readSubmissionSourcePage,
} from '../shared/gf-submission.js';
import { collectStepStates } from '../shared/step-state.js';
import { SUBSTEP_CURRENT_ARIA_VALUE } from './sidebar-constants.js';
import {
	readPrimaryAccordionState,
	readMaxVisitedPage,
	writeMaxVisitedPage,
	writePrimaryAccordionState,
} from './sidebar-runtime-store.js';
import {
	toNonNegativeInteger,
	toPositiveInteger,
} from './sidebar-utils.js';

const conditionalPageVisibilityByFormId = new Map();
const registeredConditionalPageActionForms = new Set();

/**
* Responsibility:
* - Build normalized runtime step entries from inline config.
*
* Touches:
* - Reads `config.steps` values and sanitizes page/label keys.
*
* Guarantees:
* - Returns sorted unique step entries keyed by page number.
*/
function buildRuntimeSteps(configSteps) {
	const runtimeSteps = Array.isArray(configSteps) ? configSteps : [];
	const byPage = new Map();

	runtimeSteps.forEach((entry) => {
		if (!entry || typeof entry !== 'object') {
			return;
		}

		const pageNumber = toPositiveInteger(entry.pageNumber);
		if (pageNumber <= 0) {
			return;
		}

		const label = String(entry.label || '').trim() || `Step ${pageNumber}`;
		byPage.set(pageNumber, {
			pageNumber,
			label,
		});
	});

	return Array.from(byPage.values()).sort((a, b) => a.pageNumber - b.pageNumber);
}

/**
* Responsibility:
* - Derive page numbers from rendered Gravity Forms page nodes.
*
* Touches:
* - Reads `.gform_page` elements and parses ids via `getPageNumber()`.
*
* Guarantees:
* - Returns unique sorted positive page numbers.
*/
function getDomStepNumbers(form) {
	if (!(form instanceof HTMLElement)) {
		return [];
	}

	const pageNumbers = Array.from(form.querySelectorAll('.gform_page'))
		.map((page) => getPageNumber(page))
		.filter((pageNumber) => pageNumber > 0);

	return Array.from(new Set(pageNumbers)).sort((a, b) => a - b);
}

/**
* Responsibility:
* - Build final step definitions from runtime config and rendered form fallback.
*
* Touches:
* - Merges `buildRuntimeSteps()` with rendered page numbers for deterministic coverage.
*
* Guarantees:
* - Always returns page-sorted step entries for every visible GF page.
*/
function buildStepDefinitions(config, form) {
	const runtimeSteps = buildRuntimeSteps(config?.steps);
	const byPage = new Map(runtimeSteps.map((step) => [step.pageNumber, step]));
	const domPageNumbers = getDomStepNumbers(form);

	domPageNumbers.forEach((pageNumber) => {
		if (byPage.has(pageNumber)) {
			return;
		}

		byPage.set(pageNumber, {
			pageNumber,
			label: `Step ${pageNumber}`,
		});
	});

	return Array.from(byPage.values()).sort((a, b) => a.pageNumber - b.pageNumber);
}

/**
* Responsibility:
* - Build grouped primary-step definitions from runtime config with flat-step fallback support.
*
* Touches:
* - Reads `config.primarySteps`, `config.steps`, and rendered DOM page numbers.
*
* Guarantees:
* - Returns grouped primary-step entries sorted by start page.
* - Falls back to one group per flat page when no grouped config is available.
*/
function buildPrimaryStepDefinitions(config, form) {
	const flatSteps = buildStepDefinitions(config, form);
	const flatStepsByPage = new Map(flatSteps.map((step) => [step.pageNumber, step]));
	const runtimePrimarySteps = Array.isArray(config?.primarySteps) ? config.primarySteps : [];

	const normalizedPrimarySteps = runtimePrimarySteps.map((entry, index) => {
		if (!entry || typeof entry !== 'object') {
			return null;
		}

		const pages = buildRuntimeSteps(entry.pages);
		if (pages.length <= 0) {
			return null;
		}

		const normalizedPages = pages.map((page) => flatStepsByPage.get(page.pageNumber) || page);
		const startPage = toPositiveInteger(entry.startPage) || normalizedPages[0].pageNumber;
		const label = String(entry.label || normalizedPages[0]?.label || `Step ${startPage}`).trim() || `Step ${startPage}`;

		return {
			index: toPositiveInteger(entry.index) || (index + 1),
			label,
			startPage,
			pages: normalizedPages,
		};
	}).filter(Boolean);

	if (normalizedPrimarySteps.length > 0) {
		return normalizedPrimarySteps.sort((a, b) => a.startPage - b.startPage);
	}

	return flatSteps.map((step, index) => ({
		index: index + 1,
		label: step.label,
		startPage: step.pageNumber,
		pages: [step],
	}));
}

/**
* Responsibility:
* - Persist the latest GF conditional-page visibility snapshot for one form id.
*
* Touches:
* - Stores per-page-field visibility booleans in module state keyed by form id.
*
* Guarantees:
* - Replaces prior visibility state for the form with the latest evaluated snapshot.
*/
function writeConditionalPageSnapshot(formId, pages) {
	const normalizedFormId = toPositiveInteger(formId);
	if (normalizedFormId <= 0) {
		return;
	}

	const visibilityByFieldId = new Map();
	(Array.isArray(pages) ? pages : []).forEach((page) => {
		const fieldId = toNonNegativeInteger(page?.fieldId);
		if (fieldId <= 0) {
			return;
		}

		visibilityByFieldId.set(fieldId, page?.isVisible !== false);
	});

	conditionalPageVisibilityByFormId.set(String(normalizedFormId), visibilityByFieldId);
}

/**
* Responsibility:
* - Update one GF conditional-page visibility flag after a visible/hidden action fires.
*
* Touches:
* - Mutates the stored per-form page-field visibility map in module state.
*
* Guarantees:
* - Stores the latest boolean visibility state for the provided page field id.
*/
function writeConditionalPageVisibility(formId, page, isVisible) {
	const normalizedFormId = toPositiveInteger(formId);
	if (normalizedFormId <= 0) {
		return;
	}

	const fieldId = toNonNegativeInteger(page?.fieldId);
	if (fieldId <= 0) {
		return;
	}

	const stateKey = String(normalizedFormId);
	const visibilityByFieldId = conditionalPageVisibilityByFormId.get(stateKey) ?? new Map();
	visibilityByFieldId.set(fieldId, Boolean(isVisible));
	conditionalPageVisibilityByFormId.set(stateKey, visibilityByFieldId);
}

/**
* Responsibility:
* - Resolve visible page numbers from the latest GF conditional-page action state.
*
* Touches:
* - Reads module-level visibility state and maps page-field ids back to rendered page nodes.
*
* Guarantees:
* - Returns unique sorted visible page numbers including page 1.
*/
function resolveVisiblePageNumbersFromConditionalState(form, formId) {
	if (!(form instanceof HTMLElement)) {
		return [];
	}

	const visibilityByFieldId = conditionalPageVisibilityByFormId.get(String(toPositiveInteger(formId)));
	if (!(visibilityByFieldId instanceof Map) || visibilityByFieldId.size <= 0) {
		return [];
	}

	const visiblePageNumbers = new Set();
	if (findPageElement(form, 1) instanceof HTMLElement) {
		visiblePageNumbers.add(1);
	}

	visibilityByFieldId.forEach((isVisible, fieldId) => {
		if (isVisible !== true) {
			return;
		}

		const pageElement = findPageByFieldId(form, fieldId);
		const pageNumber = getPageNumber(pageElement);
		if (pageNumber > 0) {
			visiblePageNumbers.add(pageNumber);
		}
	});

	return Array.from(visiblePageNumbers).sort((a, b) => a - b);
}

/**
* Responsibility:
* - Resolve visible page numbers from GF's rendered `data-conditional-logic` markers.
*
* Touches:
* - Reads `.gform_page[data-conditional-logic]` attributes written by GF page conditional logic.
*
* Guarantees:
* - Returns unique sorted visible page numbers including page 1 when markers are available.
*/
function resolveVisiblePageNumbersFromDomState(form) {
	if (!(form instanceof HTMLElement)) {
		return [];
	}

	const visiblePageNumbers = new Set();
	let hasConditionalVisibilityMarkers = false;

	Array.from(form.querySelectorAll('.gform_page')).forEach((page) => {
		const pageNumber = getPageNumber(page);
		if (pageNumber <= 0) {
			return;
		}

		if (pageNumber === 1) {
			visiblePageNumbers.add(1);
			return;
		}

		const conditionalVisibility = String(page.getAttribute('data-conditional-logic') || '').trim().toLowerCase();
		if (conditionalVisibility === '') {
			return;
		}

		hasConditionalVisibilityMarkers = true;
		if (conditionalVisibility !== 'hidden') {
			visiblePageNumbers.add(pageNumber);
		}
	});

	if (!hasConditionalVisibilityMarkers) {
		return [];
	}

	return Array.from(visiblePageNumbers).sort((a, b) => a - b);
}

/**
* Responsibility:
* - Resolve the sidebar-visible GF page numbers from GF conditional-page state.
*
* Touches:
* - Reads stored GF action state first, then DOM visibility markers, then static DOM page fallback.
*
* Guarantees:
* - Returns unique sorted page numbers aligned with the latest available GF visibility truth.
*/
export function resolveVisibleStepNumbers(form, formId) {
	const conditionalStatePageNumbers = resolveVisiblePageNumbersFromConditionalState(form, formId);
	if (conditionalStatePageNumbers.length > 0) {
		return conditionalStatePageNumbers;
	}

	const domStatePageNumbers = resolveVisiblePageNumbersFromDomState(form);
	if (domStatePageNumbers.length > 0) {
		return domStatePageNumbers;
	}

	return getDomStepNumbers(form);
}

/**
* Responsibility:
* - Filter grouped primary-step metadata down to the currently visible GF page flow.
*
* Touches:
* - Intersects grouped primary-step page metadata with live visible page numbers.
*
* Guarantees:
* - Returns only primary steps that still contain at least one visible GF page.
*/
function filterVisiblePrimarySteps(primarySteps, visiblePageNumbers) {
	const visiblePageNumberSet = new Set(Array.isArray(visiblePageNumbers) ? visiblePageNumbers : []);

	return (Array.isArray(primarySteps) ? primarySteps : []).map((step) => {
		if (!step || typeof step !== 'object') {
			return null;
		}

		const visiblePages = (Array.isArray(step.pages) ? step.pages : []).filter((page) => {
			return visiblePageNumberSet.has(toPositiveInteger(page?.pageNumber));
		});
		if (visiblePages.length <= 0) {
			return null;
		}

		return {
			...step,
			targetPage: visiblePages[0].pageNumber,
			pages: visiblePages,
		};
	}).filter(Boolean);
}

/**
* Responsibility:
* - Resolve current page number for sidebar sync.
*
* Touches:
* - Reads shared app state first, then GF source-page hidden input fallback.
*
* Guarantees:
* - Returns positive page number or `0` when unavailable.
*/
export function resolveCurrentPage(app, form) {
	const fromAppState = readCurrentPageNumber(app);
	if (fromAppState > 0) {
		return fromAppState;
	}

	const formId = getFormIdFromElement(form);
	if (formId <= 0) {
		return 0;
	}

	return readSubmissionSourcePage(form, formId);
}

/**
* Responsibility:
* - Build a per-page state lookup for the currently visible grouped configurator flow.
*
* Touches:
* - Flattens grouped pages and delegates state resolution to `collectStepStates()`.
*
* Guarantees:
* - Returns a page-number keyed map that covers all visible grouped pages exactly once.
*/
function buildPageStateMap(form, config, primarySteps, currentPage, maxVisitedPage) {
	const byPage = new Map();
	(Array.isArray(primarySteps) ? primarySteps : []).forEach((step) => {
		(Array.isArray(step.pages) ? step.pages : []).forEach((page) => {
			const pageNumber = toPositiveInteger(page?.pageNumber);
			if (pageNumber <= 0 || byPage.has(pageNumber)) {
				return;
			}

			byPage.set(pageNumber, page);
		});
	});

	const pageStates = collectStepStates(
		form,
		config,
		Array.from(byPage.values()),
		currentPage,
		maxVisitedPage
	);

	return new Map(pageStates.map((pageState) => [toPositiveInteger(pageState?.pageNumber), pageState]));
}

/**
* Responsibility:
* - Decide whether one grouped primary step still exposes nested substeps.
*
* Touches:
* - Reads the normalized `substeps` array on one grouped step-state entry.
*
* Guarantees:
* - Returns `true` only when the step currently has at least one visible substep.
*/
function primaryStepHasSubsteps(stepState) {
	return Array.isArray(stepState?.substeps) && stepState.substeps.length > 0;
}

/**
* Responsibility:
* - Resolve the currently active primary-step target from grouped sidebar state.
*
* Touches:
* - Reads the grouped step-state array generated for the current render.
*
* Guarantees:
* - Returns the current primary step target only when that step also has nested substeps.
*/
function resolveCurrentPrimaryTarget(primaryStepStates) {
	const currentPrimaryStep = (Array.isArray(primaryStepStates) ? primaryStepStates : []).find((step) => {
		return step.current === true && primaryStepHasSubsteps(step);
	});

	return toPositiveInteger(currentPrimaryStep?.targetPage);
}

/**
* Responsibility:
* - Resolve which grouped primary step should render expanded for the current sidebar state.
*
* Touches:
* - Reads manual accordion state first, then auto-expands the current primary step when the page context changes.
*
* Guarantees:
 * - Returns the normalized accordion state that should be committed for the current render.
 */
function resolveExpandedPrimaryAccordionState(formId, primaryStepStates) {
	const safePrimaryStepStates = Array.isArray(primaryStepStates) ? primaryStepStates : [];
	const currentPrimaryTargetPage = resolveCurrentPrimaryTarget(safePrimaryStepStates);
	const accordionState = readPrimaryAccordionState(formId);

	if (accordionState.manualSelection === true && accordionState.currentPrimaryTargetPage === currentPrimaryTargetPage) {
		if (accordionState.expandedTargetPage <= 0) {
			return {
				expandedTargetPage: 0,
				currentPrimaryTargetPage,
				manualSelection: true,
			};
		}

		const manuallyExpandedStep = safePrimaryStepStates.find((step) => {
			return toPositiveInteger(step?.targetPage) === accordionState.expandedTargetPage
				&& primaryStepHasSubsteps(step);
		});

		if (manuallyExpandedStep) {
			return {
				expandedTargetPage: accordionState.expandedTargetPage,
				currentPrimaryTargetPage,
				manualSelection: true,
			};
		}
	}

	if (currentPrimaryTargetPage > 0) {
		return {
			expandedTargetPage: currentPrimaryTargetPage,
			currentPrimaryTargetPage,
			manualSelection: false,
		};
	}

	if (accordionState.expandedTargetPage > 0) {
		const rememberedStep = safePrimaryStepStates.find((step) => {
			return toPositiveInteger(step?.targetPage) === accordionState.expandedTargetPage
				&& primaryStepHasSubsteps(step);
		});
		if (rememberedStep) {
			return {
				expandedTargetPage: accordionState.expandedTargetPage,
				currentPrimaryTargetPage,
				manualSelection: accordionState.manualSelection === true,
			};
		}
	}

	return {
		expandedTargetPage: 0,
		currentPrimaryTargetPage: 0,
		manualSelection: false,
	};
}

/**
* Responsibility:
* - Decide the final sidebar status icon type from canonical grouped/page state only.
*
* Touches:
* - Consumes only explicit `attention` and `complete` state provided by the resolved view model.
*
* Guarantees:
* - Returns one of `attention`, `complete`, or an empty string.
*/
function resolveStepButtonIconType({
	complete = false,
	attention = false,
} = {}) {
	if (attention === true) {
		return 'attention';
	}

	if (complete === true) {
		return 'complete';
	}

	return '';
}

/**
* Responsibility:
* - Decide whether a grouped step target is currently clickable.
*
* Touches:
* - Consumes only current-page and visited-page state.
*
* Guarantees:
* - Allows backward jumps freely and forward jumps only to already visited pages.
*/
function isStepClickable(targetPage, currentPage, maxVisitedPage) {
	if (targetPage <= 0 || currentPage <= 0) {
		return false;
	}

	if (targetPage < currentPage) {
		return true;
	}

	if (targetPage > currentPage && targetPage <= maxVisitedPage) {
		return true;
	}

	return false;
}

/**
* Responsibility:
* - Resolve the final render state for one nested substep button.
*
* Touches:
* - Consumes flattened per-page state and grouped navigation context.
*
* Guarantees:
* - Returns a renderer-ready substep model with explicit icon/click/aria fields.
*/
function buildResolvedSubstepState(substepState, currentPage, maxVisitedPage) {
	const targetPage = toPositiveInteger(substepState?.pageNumber);
	const current = substepState?.current === true;
	const clickable = isStepClickable(targetPage, currentPage, maxVisitedPage) || current;

	return {
		...substepState,
		targetPage,
		clickable,
		disabled: !clickable,
		currentAriaValue: current ? SUBSTEP_CURRENT_ARIA_VALUE : '',
		iconType: resolveStepButtonIconType({
			complete: substepState?.complete === true,
			attention: substepState?.attention === true,
		}),
	};
}

/**
* Responsibility:
* - Resolve the final render state for one grouped primary step.
*
* Touches:
* - Consumes grouped pages, flattened per-page state, and current accordion context.
*
* Guarantees:
* - Returns a renderer-ready primary-step model with explicit substep, icon, and expansion fields.
*/
function buildResolvedPrimaryStepState(step, pageStateMap, currentPage, maxVisitedPage, formId) {
	const pageStates = (Array.isArray(step.pages) ? step.pages : [])
		.map((page) => pageStateMap.get(toPositiveInteger(page?.pageNumber)))
		.filter(Boolean);
	const targetPage = toPositiveInteger(step?.targetPage || step?.startPage);
	const current = pageStates.some((pageState) => pageState.current === true);
	const visited = pageStates.some((pageState) => pageState.visited === true);
	const attention = pageStates.some((pageState) => pageState.attention === true);
	const complete = pageStates.length > 0 && pageStates.every((pageState) => pageState.complete === true);
	const substeps = pageStates.slice(1).map((substepState) => {
		return buildResolvedSubstepState(substepState, currentPage, maxVisitedPage);
	});
	const clickable = isStepClickable(targetPage, currentPage, maxVisitedPage) || current;

	return {
		...step,
		targetPage,
		current,
		visited,
		attention,
		complete,
		clickable,
		disabled: !clickable,
		currentAriaValue: current ? 'step' : '',
		iconType: resolveStepButtonIconType({
			complete,
			attention,
		}),
		hasSubsteps: substeps.length > 0,
		substeps,
		panelId: substeps.length > 0 ? `sd-configurator-step-panel-${toPositiveInteger(formId)}-${targetPage}` : '',
		expanded: false,
	};
}

/**
* Responsibility:
* - Register GF page-conditional-logic action listeners for sidebar visibility updates.
*
* Touches:
* - Hooks `gform_frontend_pages_evaluated_{formId}`, `gform_frontend_page_visible_{formId}`, and `gform_frontend_page_hidden_{formId}`.
*
* Guarantees:
* - Each form id registers these listeners at most once.
*/
export function registerConditionalPageActionListeners(app) {
	const formId = toPositiveInteger(app?.config?.formId);
	if (formId <= 0) {
		return;
	}

	const registrationKey = String(formId);
	if (registeredConditionalPageActionForms.has(registrationKey)) {
		return;
	}

	const addAction = window?.gform?.addAction;
	if (typeof addAction !== 'function') {
		return;
	}

	addAction(`gform_frontend_pages_evaluated_${formId}`, (pages, eventFormId) => {
		if (toPositiveInteger(eventFormId) !== formId) {
			return;
		}

		writeConditionalPageSnapshot(formId, pages);
	});

	addAction(`gform_frontend_page_visible_${formId}`, (page, eventFormId) => {
		if (toPositiveInteger(eventFormId) !== formId) {
			return;
		}

		writeConditionalPageVisibility(formId, page, true);
	});

	addAction(`gform_frontend_page_hidden_${formId}`, (page, eventFormId) => {
		if (toPositiveInteger(eventFormId) !== formId) {
			return;
		}

		writeConditionalPageVisibility(formId, page, false);
	});

	registeredConditionalPageActionForms.add(registrationKey);
}

/**
* Responsibility:
* - Build the single renderer-owned sidebar view model for the current configurator state.
*
* Touches:
* - Reads grouped step config, conditional visibility, current page, visited runtime state, and accordion runtime state.
*
* Guarantees:
* - Returns one explicit view model that the renderer can consume without re-inferring business state from the DOM.
*/
export function buildSidebarViewModel(app, form) {
	const formId = toPositiveInteger(app?.config?.formId || getFormIdFromElement(form));
	const currentPage = resolveCurrentPage(app, form);
	const maxVisitedPage = Math.max(readMaxVisitedPage(formId), currentPage);
	const visibleStepNumbers = resolveVisibleStepNumbers(form, formId);
	const primarySteps = filterVisiblePrimarySteps(
		buildPrimaryStepDefinitions(app?.config, form),
		visibleStepNumbers
	);
	const emptyAccordionState = resolveExpandedPrimaryAccordionState(formId, []);

	if (primarySteps.length <= 0) {
		return {
			formId,
			currentPage,
			maxVisitedPage,
			visibleStepNumbers,
			expandedTargetPage: emptyAccordionState.expandedTargetPage,
			accordionState: emptyAccordionState,
			primaryStepStates: [],
		};
	}

	const pageStateMap = buildPageStateMap(form, app.config, primarySteps, currentPage, maxVisitedPage);
	const basePrimaryStepStates = primarySteps.map((step) => {
		return buildResolvedPrimaryStepState(step, pageStateMap, currentPage, maxVisitedPage, formId);
	});
	const accordionState = resolveExpandedPrimaryAccordionState(formId, basePrimaryStepStates);
	const expandedTargetPage = accordionState.expandedTargetPage;
	const primaryStepStates = basePrimaryStepStates.map((stepState) => ({
		...stepState,
		expanded: stepState.hasSubsteps === true && stepState.targetPage === expandedTargetPage,
	}));

	return {
		formId,
		currentPage,
		maxVisitedPage,
		visibleStepNumbers,
		expandedTargetPage,
		accordionState,
		primaryStepStates,
	};
}

/**
* Responsibility:
* - Commit the resolved sidebar runtime state after a pure view-model pass finishes.
*
* Touches:
* - Writes the remembered max-visited page and grouped accordion state for the current form id.
*
* Guarantees:
* - Persists only the explicit state already resolved into the provided sidebar view model.
*/
export function commitSidebarViewModelRuntime(viewModel) {
	const formId = toPositiveInteger(viewModel?.formId);
	if (formId <= 0) {
		return;
	}

	writeMaxVisitedPage(formId, viewModel?.maxVisitedPage);
	writePrimaryAccordionState(formId, viewModel?.accordionState);
}
